import {
  OreDatePicker,
  OreInput,
  OreSelect,
  OreSelectPlaceholder,
} from '@edenredespana/oreneta'
import { ChangeEvent, ReactNode } from 'react'

import { FilterResultModel } from '@/Shared/domain'

interface IInputType {
  id: string
  type: 'text' | 'number' | 'select' | 'date'
  label: string
  placeholder?: string
  options?: { label: string; value: string }[]
  dateMinErrorMessage?: string

  minDate?: string
  maxDate?: string

  setValue: (filters: Array<FilterResultModel | undefined>) => void
  currentValue: Array<FilterResultModel | undefined>
  index: number
  children?: ReactNode
}

export const InputType = ({
  id,
  type,
  label,
  placeholder,
  options,
  dateMinErrorMessage,
  setValue,
  currentValue,
  index,
  minDate,
  maxDate,
  children,
}: IInputType): JSX.Element => {
  const isIsoDate = (value: string): boolean => /^\d{4}-\d{2}-\d{2}$/.test(value)

  // Si minDate referencia otro filtro (por ejemplo "beginDate"), resolvemos su valor actual.
  const minDateFromFilter = minDate
    ? currentValue.find(filter => filter?.name === minDate)?.value
    : undefined

  const resolvedMinDate =
    minDateFromFilter || (minDate && isIsoDate(minDate) ? minDate : undefined)

  const currentDateValue = currentValue[index]?.value ?? ''
  const hasMinDateError =
    type === 'date' && !!minDateFromFilter && !!currentDateValue && currentDateValue < minDateFromFilter

  const validateDate = (date: string): string => {
    // Permite escritura libre; solo corrige cuando la fecha ya tiene formato completo YYYY-MM-DD.
    if (!isIsoDate(date)) return date

    if (resolvedMinDate && date < resolvedMinDate) return resolvedMinDate
    if (maxDate && date > maxDate) return maxDate
    return date
  }

  const updateValue =
    (index: number) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const newValue = [...currentValue]

      newValue[index] = {
        name: id,
        value: type !== 'date' ? event.target.value : validateDate(event.target.value),
      }
      setValue(newValue)
    }

  // Para inputs fecha guardamos lo escrito tal cual y validamos al salir del campo (onBlur).
  const updateDateRawValue =
    (index: number) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const newValue = [...currentValue]
      newValue[index] = {
        name: id,
        value: event.target.value,
      }
      setValue(newValue)
    }

  const validateDateOnBlur =
    (index: number) => (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const newValue = [...currentValue]
      newValue[index] = {
        name: id,
        value: validateDate(event.target.value),
      }
      setValue(newValue)
    }

  // Si se pasa un child personalizado (ej: OreMultiSelect) lo renderizamos directamente
  if (children) {
    return <>{children}</>
  }

  switch (type) {
    case 'text':
    case 'number':
      return (
        <OreInput
          label={label}
          type={type}
          placeholder={placeholder}
          onChange={updateValue(index)}
          key={id}
          value={currentValue[index]?.value ?? ''}
        />
      )
    case 'select':
      return (
        <OreSelect
          label={label}
          onChange={updateValue(index)}
          key={id}
          value={currentValue[index]?.value ?? ''}
        >
          {placeholder ? <OreSelectPlaceholder label={placeholder} /> : null}
          {options
            ? options.map((o, i) => (
                <option value={o.value} key={id + i}>
                  {o.label}
                </option>
              ))
            : null}
        </OreSelect>
      )
    case 'date':
      return (
        <OreDatePicker
          label={label}
          key={id}
          onChange={updateDateRawValue(index)}
          onBlur={validateDateOnBlur(index)}
          value={currentValue[index]?.value ?? ''}
          min={resolvedMinDate}
          max={maxDate}
          hasError={hasMinDateError}
          errorMessage={hasMinDateError ? dateMinErrorMessage : undefined}
        />
      )
    default:
      return (
        <OreInput
          label={label}
          type={type}
          onChange={updateValue(index)}
          placeholder={placeholder}
          key={id}
          value={currentValue[index]?.value ?? ''}
        />
      )
  }
}
